import React, {
  useState,
  useContext,
} from 'react';
import { useRouteMatch } from 'react-router-dom';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import {
  Checkbox,
  Collapse,
  Grid,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { ExpandMore } from '@material-ui/icons';
import {
  LeadsIcon,
  CustomersIcon,
} from '../../assets/icons';
import {
  getOpportunityName,
  toCapitalize,
  toTitleCase,
} from '../../utils';
// CPI-1197: Used to grant Sales Admin L1-level access (country-agnostic)
import { ROLE_HEADQUARTERS, hasL2ViewAllAccess } from '../../data/roles';
import { COUNTRY_VN } from '../../localization/features/constants';
import useStyles from './styles';
import { AuthContext } from '../../contexts';
import CustomerRow from './CustomerRow';

export default function ViewPage(props) {
  const classes = useStyles();

  const authContext = useContext(AuthContext);
  const { country, user: authUser } = authContext.state;
  // CPI-1197: Grant Sales Admin L1-level access
  const shouldShowAll = hasL2ViewAllAccess(authUser?.role, country?.features);
  // Vietnam has no leads module, so the drawer shows the customer
  // duplicates list only - no leads section, no merge flow, and an intro
  // message without the "merge or qualify this lead" call to action.
  const isVietnam = country?.country_code === COUNTRY_VN;

  const [activeStates, setActiveStates] = useState({
    leads: true,
    customers: true,
  });

  const {
    user,
    users,
    projects,
    duplicateCustomers,
    duplicateLeads,
    selectedLeads,
    setSelectedLeads,
  } = props;

  const match = useRouteMatch();
  const currentId = match.params.id || '';

  const { t } = useTranslation();
  const icLabelText = t(`component.label.ic.${country.ic_field}`, country.ic_field.toUpperCase());

  const toggleState = (name) => () => {
    setActiveStates((s) => ({ ...s, [name]: !s[name] }));
  };

  const getUserName = (id) => {
    const u = users[id];
    if (u) {
      if (u.name) {
        return u.name;
      }
      if (u.preferred_name) {
        return u.preferred_name;
      }
    }
    return id;
  };

  const handleSelectLeads = (e, lead) => {
    const { checked } = e.target;
    if (checked) {
      setSelectedLeads((prev) => ([...prev, lead]));
    } else {
      setSelectedLeads((prev) => prev.filter((l) => l.id !== lead.id));
    }
  };

  const renderProject = (type, item) => {
    if (type === 'leads' && item.project_ids) {
      const list = [...item.project_ids];
      const firstProject = list.splice(0, 1);
      return (
        <>
          {
            firstProject.length > 0 && React.Children.toArray(firstProject.map((id) => (
              <div className={classes.projectWrapper}>
                <Typography variant="caption" className={`${classes.project} ${classes.ellipsis}`}>
                  { (projects[id] && projects[id].name) || id }
                </Typography>
              </div>
            )))
          }
          {
            list.length > 0 && (
              <div className={classes.projectWrapper}>
                <Typography variant="caption" className={`${classes.project} ${classes.ellipsis} subCounts`}>
                  {`+ ${list.length} `}
                </Typography>
              </div>
            )
          }
        </>
      );
    }

    if (type === 'customers' && item.opportunities && item.opportunities.length > 0) {
      const newestOpp = item.opportunities[0];
      if (newestOpp && newestOpp.project_id) {
        const name = getOpportunityName(newestOpp, projects);
        return (
          <div className={classes.projectWrapper}>
            <Typography variant="caption" className={`${classes.project} ${classes.ellipsis}`}>
              { name }
            </Typography>
          </div>
        );
      }
    }
    return null;
  };

  const renderAssignee = (type, item) => {
    if (type === 'leads') {
      return getUserName(item.assigned_to) || t('component.label.unassigned', 'Unassigned');
    }

    if (type === 'customers' && item.opportunities && item.opportunities.length > 0) {
      return (
        item.opportunities[0] && item.opportunities[0].project_id
          ? (getUserName(item.opportunities[0].assigned_to) || t('component.label.unassigned', 'Unassigned'))
          : ''
      );
    }
    return '';
  };

  const renderTableRow = (items, item, type) => {
    if (_.isEmpty(item)) {
      return null;
    }
    if (item.deleted_at) {
      return null;
    }

    const isHQ = user.role === ROLE_HEADQUARTERS;

    return (
      <TableRow key={item.id}>
        <TableCell>
          <div className={classes.checkboxCell}>
            {
              type === 'leads' && items.length > 1 && (
                <Checkbox
                  disableRipple
                  className={classes.checkbox}
                  disabled={
                    item.id === currentId
                    || (
                      selectedLeads.length === 3
                      && !(selectedLeads.some((selected) => selected.id === item.id))
                    )
                    || (!isHQ && item.assigned_to !== user.id)
                  }
                  checked={(
                    selectedLeads.some((selected) => (
                      selected.id === item.id
                    ))
                  )}
                  onChange={(e) => handleSelectLeads(e, {
                    ...item,
                    msisdn: item.msisdn || '',
                    mobile_country_code: item.mobile_country_code || '',
                    mobile: item.mobile || '',
                    assigned_to: item.assigned_to || '',
                  })}
                />
              )
            }
          </div>
        </TableCell>
        <TableCell>
          {
            // LMS-5183: masked duplicate outside the caller's scope — no name and no link
            type === 'customers' && item.outOfScope ? (
              <Typography variant="subtitle2" className={`${classes.name} ${classes.ellipsis}`}>
                {t('component.label.existing_customer_out_of_scope', 'Existing customer (outside your access)')}
              </Typography>
            ) : (
              <Link
                variant="subtitle2"
                className={`${classes.name} ${classes.ellipsis}`}
                href={`/${type}/${item.id}`}
              >
                { toCapitalize(item.name) || t('component.text.n/a', 'n/a') }
              </Link>
            )
          }
        </TableCell>
        <TableCell
          className={`${classes.projectCell} ${classes.value}`}
        >
          {
            type === 'customers' && !item.haveAccess
              ? ' '
              : renderProject(type, item)
          }
        </TableCell>
        <TableCell>
          <Typography variant="body2" className={`${classes.assignee} ${classes.ellipsis} ${classes.value}`}>
            { renderAssignee(type, item) }
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">{ item.msisdn || item.mobile || item.phone }</Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">{ item.email }</Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">{ item[country.ic_field] }</Typography>
        </TableCell>
      </TableRow>
    );
  };

  const renderCustomerCategory = () => {
    const name = 'customers';
    const info = t('duplicate_drawer.category.customers_info', 'Customer duplicates cannot be merged with leads.');

    return (
      <section className={classes.section}>
        <Grid container alignItems="center">
          <IconButton
            size="small"
            className={`${classes.expand} ${activeStates[name] ? classes.expandOpen : ''}`}
            onClick={toggleState(name)}
          >
            <ExpandMore />
          </IconButton>
          <img
            alt=""
            className={classes.icon}
            src={CustomersIcon}
          />
          <Typography variant="h6">
            {`${toTitleCase(t(`entity.${name}`, name))} (${Array.isArray(duplicateCustomers) ? duplicateCustomers.length : 0})`}
          </Typography>
        </Grid>
        <Collapse in={activeStates[name]} timeout="auto">
          {
            !isVietnam && (
              <Typography variant="body2" className={classes.info}>
                { info }
              </Typography>
            )
          }
          <div className={classes.tableContainer}>
            {
              Array.isArray(duplicateCustomers) && duplicateCustomers.length > 0 && (
                <Table className={classes.table}>
                  <TableHead>
                    <TableRow>
                      <TableCell />
                      {
                        [
                          { label: toTitleCase(t('entity.name', 'name')), width: '30%' },
                          {
                            label: t('component.label.newest_opportunity', 'Newest opportunity'),
                            width: '35%',
                          },
                          { label: t('component.label.assignee', 'Assignee'), width: '20%' },
                          { label: t('component.label.mobile', 'Mobile'), width: '10%' },
                          { label: t('component.label.email', 'Email'), width: '10%' },
                          { label: country.ic_field.toUpperCase(), width: '10%' },
                        ].map((cell) => (
                          <TableCell key={cell.label} style={{ width: cell.width }}>
                            <Typography className={classes.th}>
                              { cell.label }
                            </Typography>
                          </TableCell>
                        ))
                      }
                    </TableRow>
                  </TableHead>
                  <TableBody className={classes.tbody}>
                    {
                      duplicateCustomers.map((customer) => (
                        <CustomerRow
                          key={customer.id}
                          customer={customer}
                          shouldShowAll={shouldShowAll}
                          renderRow={(customerWithData) => (
                            renderTableRow(duplicateCustomers, customerWithData, 'customers')
                          )}
                        />
                      ))
                    }
                  </TableBody>
                </Table>
              )
            }
          </div>
        </Collapse>
      </section>
    );
  };

  const renderCategory = (name, items) => {
    let info = '';

    switch (name) {
      case 'customers':
        info = t('duplicate_drawer.category.customers_info', 'Customer duplicates cannot be merged with leads.');
        break;
      default:
        info = t('duplicate_drawer.category.default_info', 'Select up to 3 leads to merge and click next to choose fields.');
        break;
    }

    return (
      <section className={classes.section}>
        <Grid container alignItems="center">
          <IconButton
            size="small"
            className={`${classes.expand} ${activeStates[name] ? classes.expandOpen : ''}`}
            onClick={toggleState(name)}
          >
            <ExpandMore />
          </IconButton>
          <img
            alt=""
            className={classes.icon}
            src={name === 'leads' ? LeadsIcon : CustomersIcon}
          />
          <Typography variant="h6">
            {`${toTitleCase(t(`entity.${name}`, name))} (${items.length})`}
          </Typography>
          {
            name === 'leads' && selectedLeads.length > 0 && (
              <Typography variant="caption" className={classes.totalSelected}>
                {t('duplicate_drawer.selected_count', '{count} selected', { count: selectedLeads.length })}
              </Typography>
            )
          }
        </Grid>
        <Collapse in={activeStates[name]} timeout="auto">
          <Typography variant="body2" className={classes.info}>
            { info }
          </Typography>
          <div className={classes.tableContainer}>
            {
              items.length > 0 && (
                <Table className={classes.table}>
                  <TableHead>
                    <TableRow>
                      <TableCell />
                      {
                        [
                          { label: toTitleCase(t('entity.name', 'name')), width: '30%' },
                          {
                            label: name === 'leads'
                              ? toTitleCase(t('suffix.name', {
                                entity: t('entity.project', 'project'),
                                action: t('action.name', 'name'),
                                defaultValue: '{entity} {action}',
                              }, 'project name'))
                              : t('component.label.newest_opportunity', 'Newest opportunity'),
                            width: '35%',
                          },
                          { label: t('component.label.assignee', 'Assignee'), width: '20%' },
                          { label: t('component.label.mobile', 'Mobile'), width: '10%' },
                          { label: t('component.label.email', 'Email'), width: '10%' },
                          { label: country.ic_field.toUpperCase(), width: '10%' },
                        ].map((cell) => (
                          <TableCell key={cell.label} style={{ width: cell.width }}>
                            <Typography className={classes.th}>
                              { cell.label }
                            </Typography>
                          </TableCell>
                        ))
                      }
                    </TableRow>
                  </TableHead>
                  <TableBody className={classes.tbody}>
                    {
                      items.map((item) => (
                        renderTableRow(items, item, name)
                      ))
                    }
                  </TableBody>
                </Table>
              )
            }
          </div>
        </Collapse>
      </section>
    );
  };

  return (
    <>
      <Typography variant="body2" className={classes.topMessage}>
        {
          // Vietnam has no leads, so it drops the "merge or qualify
          // this lead" call to action and keeps the finding itself.
          isVietnam
            ? t(
              'duplicate_drawer.view_page.message_customers_only',
              'We found duplicates of the same mobile or email or {icLabel}.',
              { icLabel: icLabelText },
            )
            : t(
              'duplicate_drawer.view_page.message',
              'We found duplicates of the same mobile or email or {icLabel}.'
              + ' To resolve, view to merge or qualify this lead.',
              { icLabel: icLabelText },
            )
        }
        {
          // The article behind this link is about merging duplicate
          // leads, which Vietnam cannot do.
          !isVietnam && (
            <>
              &nbsp;
              <Link target="_blank" rel="noopener" href="https://support.mhub.my/hc/en-us/articles/360055635834-How-do-I-merge-duplicate-leads-">
                {t('component.label.learn_more', 'Learn more')}
              </Link>
            </>
          )
        }
      </Typography>
      { !isVietnam && renderCategory('leads', duplicateLeads) }
      { renderCustomerCategory() }
    </>
  );
}

ViewPage.propTypes = {
  user: PropTypes.instanceOf(Object),
  users: PropTypes.instanceOf(Object),
  projects: PropTypes.instanceOf(Object),
  duplicateCustomers: PropTypes.instanceOf(Object),
  duplicateLeads: PropTypes.instanceOf(Object),
  selectedLeads: PropTypes.arrayOf(PropTypes.instanceOf(Object)),
  setSelectedLeads: PropTypes.func,
};

ViewPage.defaultProps = {
  user: {},
  users: {},
  projects: {},
  duplicateCustomers: {},
  duplicateLeads: {},
  selectedLeads: [],
  setSelectedLeads: () => {},
};
